import { useEffect, useState } from 'react';
import Spinner from './spinner';
import CommentBox from '../../../components/commentBox/commentBox';
import { DesignDoc, SewDiskCharge, SewDiskFeeHourly } from '../../../types/api-types';
import { InternalArtStates } from '../../../types/art-status';
import { getSewDiskChargesForDocumentId, getSewDiskFeesHourly, saveSewDiskCharges } from '../../../api/backend';

// Styles
import styles from './sewDiskFeesModal.module.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  document?: DesignDoc;
  addSewChargeSuccess: (comment: string) => Promise<void>;
  isCustomerView: boolean;
}

const SewChargeModal: React.FC<ModalProps> = ({ isOpen, onClose, document, isCustomerView, addSewChargeSuccess }) => {
  const documentId = document?.id;
  const isApproved = document?.statusID == InternalArtStates.APPROVED;
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hours, setHours] = useState<number>(0);
  const [hourlyFeesHours, setHourlyFeesHours] = useState<SewDiskFeeHourly | undefined>(undefined);
  const [comment, setComment] = useState<string>('');
  const [totalCharges, setTotalCharges] = useState<number>(0);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [currentSewDiskCharge, setCurrentSewDiskCharge] = useState<SewDiskCharge|undefined>(undefined)
  const getFactorToGetNumOfHours = (uom:string) => {
    switch (uom) {
      case '1/2 HOUR':
        return 2;
      default:
        return 1;
    }
  }

  const factorToGetNumOfHours = hourlyFeesHours ? getFactorToGetNumOfHours(hourlyFeesHours.uom) : 0

  useEffect(() => {
    if (isOpen) {
      setError(null);
      handleFetchCharges();
    } else {
      setHours(0);
      setComment('');
      setHourlyFeesHours(undefined);
    }
  }, [isOpen]);

  const handleFetchCharges = async () => {
    if (!documentId) return;
    setProcessing(true);

    try {
      await Promise.all([
        getSewDiskFeesHourly().then(hourlyFees => { 
          if (hourlyFees) {
            setHourlyFeesHours(hourlyFees);
          }
        }),
        getSewDiskChargesForDocumentId(documentId).then(sewDiskCharges => {
          const [sewDiskCharge] = sewDiskCharges;
          setCurrentSewDiskCharge(sewDiskCharge);
        })
      ])
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }

    setProcessing(false);
  };

  const saveSewDiskCharge = async () => {
    try {
      setProcessing(true);
      setError(null);
      if (!documentId) {
        throw new Error('Document ID is required to save Sew Charge.');
      }

      totalCost ? await saveSewDiskCharges(documentId as string, totalCharges, totalCost) : await saveSewDiskCharges(documentId as string, totalCharges);
      await addSewChargeSuccess(comment);
      setComment('');
    } catch (err) {
      setError('Failed to set Sew Charge: ' + (err as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  const handleInputChange = (value: number) => {
    if (value < 0) {
      setError('Hours cannot be negative.');
      return;
    }
    const roundedValue = Math.round(value * 10) / 10; 
    if (!hourlyFeesHours)
      return;
    const numOfHours = roundedValue * factorToGetNumOfHours;
    const price = numOfHours * hourlyFeesHours.price;
    const cost = numOfHours * hourlyFeesHours.cost;
    const roundedCost = Math.round(cost * 100) / 100;
    const roundedPrice = Math.round(price * 100) / 100;
    setTotalCharges(roundedPrice); 
    setTotalCost(roundedCost); 
    setHours(Number(value));
  };

  const handleCommentChange = (value: string) => {
    setComment(value);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    saveSewDiskCharge();
  };

  function CloseIcon() {
    return (
      <button
        type="button"
        className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
      >
        <span className="sr-only">Close menu</span>
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    );
  }

  return isOpen ? (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modalContainer} relative w-auto my-auto mx-auto max-w-3xl`}>
        <div className={`${styles.modalWrapper} border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none`}>
          <div className={styles.header}>
            <span>Sew Disk Charge</span>
            <div className={styles.closeIcon} onClick={onClose}>
              <CloseIcon />
            </div>
          </div>
          <div className={styles.modalContent}>
            {error && <div className={styles.errorMessage}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className={styles.inputsContainer}>
                <div className={styles.inputWrapper}>
                  <label style={{ paddingBottom: '0px' }} htmlFor="hours">
                    Actual time (in hours):
                  </label>
                  <input
                    id="hours"
                    type="number"
                    value={hours}
                    onChange={(e) => handleInputChange(Number(e.target.value))}
                    step="0.5"
                    min={0}
                    required
                    disabled={processing}
                  />
                </div>
              </div>

              {hourlyFeesHours && <div className="flex flex-row gap-2 mb-4">
                <span className="font-medium">@ ${Math.round(hourlyFeesHours.price * 100 * factorToGetNumOfHours) / 100} per hour</span>
              </div>}

              {currentSewDiskCharge && <div className="flex flex-row gap-2 mb-4">
                <span className="font-medium">Current sew disk charge is ${currentSewDiskCharge.price}</span>
              </div>}

              <div className="flex flex-row gap-2 mb-4">
                <span className="font-medium">Updated sew disk charge:</span>
                <span>{totalCharges || "---"}</span>
              </div>

              {!isApproved && (
                <CommentBox onCommentChange={handleCommentChange} isCustomerView={isCustomerView} showTitle={false} />
              )}

              <div className={styles.confirmBtns}>
                <button className={[styles.button, styles.cancelBtn].join(' ')} onClick={onClose} disabled={processing}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className={[styles.addButton, processing ? styles.disabledBtn : ''].join(' ')}
                  disabled={processing}
                >
                  {processing ? <Spinner spinning={processing} color="#F36E29" size={{ width: '22px', height: '22px' }} /> : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default SewChargeModal;
